import { ref, computed } from 'vue'
import { useUserStore } from '@/stores/user'
import { useErrorStore } from '@/stores/error'

export default function(userLeadInfo: UserLeadInfo) {
  // store
  const userStore = useUserStore()
  const errorStore = useErrorStore()

  /**
   * 必須のリード情報がすべて入力済みかどうか
   * @description name が定義されていない（フォームに存在しない）項目と任意項目は判定対象外
   */
  const evaluateHasUserLeadInfo = (): boolean => {
    return Object.values(userLeadInfo).filter(v => v && v.name && v.validationScheme?.required).every(v => !!v.value)
  }

  // ページ表示時点で必須のリード情報が入力済みだったかのスナップショット。
  // userLeadInfo はサーバ埋め込みの view_props から setup 中に同期で埋まっているため、生成時に評価できる。
  // 入力中に反転して編集 UI が消えないよう computed にはしない
  const hasUserLeadInfo = ref(evaluateHasUserLeadInfo())

  // ユーザーのリード情報が入力（編集）モードかどうか
  const isEditModeUserLeadInfo = ref(!hasUserLeadInfo.value)

  const isUpdateUserLeadInfo = computed(() => userStore.context.isSignedIn && isEditModeUserLeadInfo.value)

  const selectCompanyLeadInfo = (companyLeadInfo: CompanyLeadInfo) => {
    if (userLeadInfo.companyName)       userLeadInfo.companyName.value = companyLeadInfo.company_name
    if (userLeadInfo.corporateAddress1) userLeadInfo.corporateAddress1.value = companyLeadInfo.address
    if (userLeadInfo.corporateNumber)   userLeadInfo.corporateNumber.value = companyLeadInfo.corporate_number
    if (userLeadInfo.corporateZipcode)  userLeadInfo.corporateZipcode.value = companyLeadInfo.zipcode
    if (userLeadInfo.corporateEmployeeNum) userLeadInfo.corporateEmployeeNum.value = companyLeadInfo.employee_num
  }

  /**
   * 会社名入力処理
   * @description 会社名を手動入力した際に、保持している会社情報を初期化する。
   * @description 会社名を入力し、表示されたサジェストを選択すると、内部的に「会社住所」等の情報を自動補完するが、その後に再度「会社名」を手動入力した場合に、前に格納した会社情報をリセットする必要があるため。
   * @param companyName 入力した会社名の文字列
   */
  const inputCompanyName = (companyName: string) => {
    if (userLeadInfo.companyName) userLeadInfo.companyName.value = companyName

    if (userLeadInfo.corporateAddress1) userLeadInfo.corporateAddress1.value = ''
    if (userLeadInfo.corporateAddress2) userLeadInfo.corporateAddress2.value = ''
    if (userLeadInfo.corporateNumber) userLeadInfo.corporateNumber.value = ''
    if (userLeadInfo.corporateZipcode)  userLeadInfo.corporateZipcode.value = ''
    if (userLeadInfo.corporateEmployeeNum) userLeadInfo.corporateEmployeeNum.value = undefined
  }

  /**
   * ユーザ情報表示モードから編集モードへ移行
   */
  const toEditModeUserLeadInfo = (): void => {
    isEditModeUserLeadInfo.value = true
  }

  /**
   * ユーザ情報の入力に不備のあるフィールドが存在する場合に、編集モードへ強制移行
   */
  const toEditModeUserLeadInfoByError = () => {
    if (hasUserLeadInfoError()) toEditModeUserLeadInfo()
  }

  /**
   * （必須項目の）ユーザ情報の入力に不備のあるフィールドが存在するかどうか
   * @returns true 入力内容にエラーがある / false エラーなし
   */
  const hasUserLeadInfoError = (): boolean => {
    return !!Object.values(userLeadInfo).filter(v => {
      if (v && v.validationScheme?.required) {
        return !!errorStore.getErrorMessagesByFieldName(v.name).length
      }
    }).length
  }

  return {
    // data
    hasUserLeadInfo,
    isEditModeUserLeadInfo,

    // computed
    isUpdateUserLeadInfo,

    // method
    selectCompanyLeadInfo,
    inputCompanyName,
    toEditModeUserLeadInfo,
    toEditModeUserLeadInfoByError,
    hasUserLeadInfoError,
  }
}
